import {noop} from '@benchling/js-utils';
import produce from 'immer';
import React, {useCallback, useRef, useState} from 'react';
import isEqual from 'react-fast-compare';

import * as analytics from 'analytics/Analytics';
import {FixedPlateModalType} from 'constants/analytics-products/SamplesAnalyticsConstants';
import {ActionOrigin, ANALYTICS_MODULE, COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import notifications from 'notifications';
import AbandonPlateMapConfirmationModal from 'samples/plate-maps/AbandonPlateMapConfirmationModal';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import DefinePlateMapStep from 'samples/plate-maps/annotate-fill-plate-modal/DefinePlateMapStep';
import {usePlateMapValidation} from 'samples/plate-maps/hooks/usePlateMapValidation';
import useWellSchemaLayers from 'samples/plate-maps/hooks/useWellSchemaLayers';
import {NoPlateMap} from 'samples/plate-maps/NoPlateMap';
import {
  DisplayLayerOptionSection,
  PLATE_MAP_MAX_WELL_SCHEMA_FIELD_LAYERS,
  PlateMap,
  SchematizedDisplayLayerMetadata,
  WELL_ROLE_ATTRIBUTE_SENTINEL,
} from 'samples/plate-maps/PlateMapConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';
import runModal from 'ui-library/runModal';
import {isServerRequestError} from 'util/Net';

const MAX_PLATE_HEIGHT = 'calc(100vh - 400px)';

export interface AnnotatePlateModalProps {
  existingPlateMap: PlateMap;
  initialSelectedWellIndices: Set<number>;
  savePlateAnnotations: ({
    annotatedSchemaFieldIds,
    callbackOnError,
    plateMap,
  }: {
    annotatedSchemaFieldIds: Set<string>;
    callbackOnError: () => void;
    plateMap: PlateMap;
  }) => Promise<void>;
  eventGroupId: string;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  plateSchemaId: string;
  cellContainerSchemaId: string;
}

/**
 * Modal with the steps to assign well information to an existing plate,
 * such as well roles or schema metadata.
 */
export default function AnnotatePlateModal({
  existingPlateMap,
  initialSelectedWellIndices,
  plateSchemaId,
  cellContainerSchemaId,
  savePlateAnnotations,
  eventGroupId,
  onRequestClose,
  onRequestSubmit,
}: AnnotatePlateModalProps): JSX.Element {
  const [plateMap, setPlateMap] = useState<PlateMap>(existingPlateMap);
  const [selectedWellSchemaFieldIds, setSelectedWellSchemaFieldIds] = useState<Set<string>>(new Set());
  const modalRef = useRef<HTMLDivElement>(null);

  const analyticsMetadata = {
    actionOrigin: ActionOrigin.INVENTORY_PLATE_PAGE_GRID_CELL_TOOLBAR,
    modalType: FixedPlateModalType.ANNOTATE_PLATE,
    plateId: plateMap.id,
    eventGroupId,
  };

  const onLoadWellSchemaFields = useCallback((schemaFieldLayers: Array<SchematizedDisplayLayerMetadata>) => {
    const schemaFieldIds = schemaFieldLayers.map(({attribute}) => attribute.key);
    if (schemaFieldIds.length <= PLATE_MAP_MAX_WELL_SCHEMA_FIELD_LAYERS) {
      setSelectedWellSchemaFieldIds(new Set(schemaFieldIds));
    }
  }, []);

  const wellSchemaLayers = useWellSchemaLayers(cellContainerSchemaId, onLoadWellSchemaFields, []);

  const {refetch, validationStatus, validationRequestFailedMessage, validatedPlateMap, validationErrors} =
    usePlateMapValidation({
      plateId: plateMap.id,
      plateMap,
      annotatedSchemaFieldIds: selectedWellSchemaFieldIds,
      plateSchemaId,
      // This modal is only used in the inventory context, where there is no document
      documentId: null,
    });

  async function handleSubmit(): Promise<void> {
    analytics.trackEventWithModule(
      COMMON_ANALYTICS_ACTIONS.SUBMITTED_MODAL,
      ANALYTICS_MODULE.SAMPLES,
      analyticsMetadata
    );
    try {
      await savePlateAnnotations({
        annotatedSchemaFieldIds: selectedWellSchemaFieldIds,
        callbackOnError: refetch ?? noop,
        plateMap,
      });
      onRequestSubmit();
    } catch (e) {
      if (isServerRequestError(e)) {
        notifications.notifyError(e);
        return;
      }
      throw e;
    }
  }

  return (
    <Modal
      title="Annotate plate"
      fullWidth={true}
      buttons={[
        {text: 'Cancel', cancel: true},
        {text: 'Save', submit: true, style: ButtonStyle.SECONDARY},
      ]}
      onRequestClose={() => {
        if (!isEqual(plateMap, existingPlateMap)) {
          runModal(AbandonPlateMapConfirmationModal, {
            onRequestClose: noop,
            onRequestSubmit: onRequestClose,
            title: 'Leave plate annotation?',
            confirmButtonText: 'Continue annotating',
          });
        } else {
          onRequestClose();
        }
      }}
      onRequestSubmit={handleSubmit}
      wrapInForm={false}
      closeableOnEscapeKey={false}
      expandHeight={true}
      ref={modalRef}
    >
      {validatedPlateMap == null ? (
        <NoPlateMap
          refetch={refetch}
          validationStatus={validationStatus}
          validationRequestFailedMessage={validationRequestFailedMessage}
        />
      ) : (
        <DefinePlateMapStep
          plateMap={plateMap}
          setPlateMap={setPlateMap}
          initialPlateMap={existingPlateMap}
          initialSelectedWellIndices={initialSelectedWellIndices}
          initialSelectedAttribute={WELL_ROLE_ATTRIBUTE_SENTINEL}
          modalRef={modalRef}
          analyticsMetadata={analyticsMetadata}
          maxPlateViewHeight={MAX_PLATE_HEIGHT}
          disabledSections={[DisplayLayerOptionSection.CONTENTS]}
          showFinalMeasurements={false}
          procedureContext={{
            procedureId: null,
            isInProcedure: false,
            procedureStudyDesignType: null,
            procedureMethodExecutionInstanceId: null,
          }}
          plateDataParameters={[]}
          selectedWellSchemaFieldIds={selectedWellSchemaFieldIds}
          wellSchemaLayers={wellSchemaLayers}
          onAddSelectedDataParameter={noop}
          onRemoveSelectedDataParameter={noop}
          onAddSelectedSchemaField={(schemaFieldId: string) => {
            setSelectedWellSchemaFieldIds(new Set([...selectedWellSchemaFieldIds, schemaFieldId]));
          }}
          onRemoveSelectedSchemaField={(schemaFieldId: string) => {
            setSelectedWellSchemaFieldIds(
              new Set([...selectedWellSchemaFieldIds].filter((id) => id !== schemaFieldId))
            );
            const newPlateMap = produce(plateMap, (draft) => {
              for (const [index, well] of draft.wells.entries()) {
                well.schemaTags[schemaFieldId] = existingPlateMap.wells[index].schemaTags[schemaFieldId];
              }
            });
            setPlateMap(newPlateMap);
          }}
          initialShouldPreviewWellRoles={true}
          validationErrors={validationErrors}
          onDismissErrors={noop}
          plateMapType={PlateMapType.RECORD}
        />
      )}
    </Modal>
  );
}
